import React, { useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Typography, CircularProgress, Button, Modal, Box, Tooltip, IconButton } from '@mui/material';
import { Download as DownloadIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { useTranslation } from '../translationContext';
import { useBody } from '../../Body/state/BodyContext';
import { ModalGeneral } from '../../Componentes/ModalGeneral';
import useMediaQuery from '@mui/material/useMediaQuery';

const EstadoSolicitudes = ({ userId = null }) => {
    const { translations, getTranslations, getTranslationsByUserId, downloadTranslation, downloadExcel } = useTranslation();
    const { setMessage, setSuccess } = useBody();
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [selectedObservacion, setSelectedObservacion] = useState('');
    const [selectedDetails, setSelectedDetails] = useState(null);
    const matches = useMediaQuery('(min-width:1185px)');

    const fetchTranslations = async () => {
        if (userId) {
            await getTranslationsByUserId(userId);
        } else {
            await getTranslations();
        }
        if (!Array.isArray(translations)) {
            setMessage('Hubo un error al intentar obtener el estado de las solicitudes.');
        }
        setLoading(false);
    };

    const handleDownloadExcel = async () => {
        try {
            const response = await downloadExcel();
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "traducciones.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setSuccess('Archivo descargado correctamente');
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            setMessage('Error al descargar el archivo');
        }
    };

    useEffect(() => {
        fetchTranslations();
    }, []);

    const handleDownloadFile = async (type, folder, fileName) => {
        try {
            const response = await downloadTranslation(type, folder, fileName);

            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            setSuccess('Archivo descargado correctamente');

        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            setMessage('Error al descargar el archivo');
        }
    };

    const handleOpenDetails = (details) => {
        setSelectedDetails(details);
        setOpenDetailsModal(true);
    }

    const handleCloseDetailsModal = () => {
        setOpenDetailsModal(false);
        setSelectedObservacion(null);
    };

    const handleOpenModal = (observacion) => {
        setSelectedObservacion(observacion);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedObservacion('');
    };

    const columns = [
        {
            header: 'Título',
            accessorKey: 'title',
            size: 120,
            Cell: ({ cell }) => (
                <Tooltip title={cell.getValue()} placement="bottom-start">
                    <span style={{
                        display: 'block',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '200px'
                    }}>
                        {cell.getValue()}
                    </span>
                </Tooltip>
            ),
        },
        {
            header: matches ? 'Estado de la traducción' : 'Estado',
            accessorKey: 'status.value',
            size: 120,
        },
        {
            header: 'Fecha del pedido | Fecha de fin',
            accessorKey: 'deadline',
            Cell: ({ cell }) => {
                const fechaPedido = cell.row.original.createdAt.split('T')[0];
                const fechaFin = cell.row.original.deadline.split('T')[0];
                return `${fechaPedido} | ${fechaFin}`;
            },
        },
        {
            header: 'Observaciones',
            accessorKey: 'observation',
            Cell: ({ cell }) => (
                cell.getValue() ? (
                    <Button
                        sx={{ textTransform: 'none' }}
                        onClick={() => handleOpenModal(cell.getValue())}>
                        <VisibilityIcon sx={{ marginRight: "7px" }} />
                        Ver Observación
                    </Button>
                ) : "Sin observaciones"
            ),
        },
        {
            header: 'Original',
            accessorKey: 'file_original.file_name',
            Cell: ({ cell }) => {
                const fileName = cell.getValue();
                const folderName = cell.row.original.file_original.folder_name;
                return fileName ? (
                    <Button
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadFile('original', folderName, fileName)}
                    >
                        Descargar
                    </Button>
                ) : "Sin archivo adjuntado";
            },
        },
        {
            header: 'Traducido',
            accessorKey: 'file_corrected.file_name',
            Cell: ({ cell }) => {
                const fileName = cell.getValue();
                const folderName = cell.row.original.file_corrected?.folder_name;
                return fileName ? (
                    <Button
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadFile('corrected', folderName, fileName)}
                    >
                        Descargar
                    </Button>
                ) : "Sin archivo adjuntado";
            },
        },
    ];

    const responsiveColumns = columns.slice(0, -4);

    responsiveColumns.push(
        {
            header: 'Detalles',
            accessorKey: 'details',
            size: 100,
            Cell: ({ cell }) => (
                <IconButton
                    onClick={() => handleOpenDetails(cell.row.original)}
                >
                    <VisibilityIcon sx={{ color: "#B71234" }} />
                </IconButton>
            ),
        },
    );

    return (
        <>
            <Box marginBottom={"10%"}>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: matches ? 'center' : 'flex-start',
                        justifyContent: matches ? 'space-between' : 'center',
                        flexDirection: matches ? 'row' : 'column',
                        padding: '0 10px',
                    }}
                >
                    <Typography
                        my={matches ? "40px" : "20px"}
                        mx="10px"
                        fontSize={matches ? "30px" : "28px"}
                    >
                        Solicitudes
                    </Typography>
                    {userId === null && (
                        <Box style={{ display: "flex", alignItems: "center", justifyContent: "right", width: "100%", marginBottom: matches ? "0" : "10px" }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadExcel}
                                sx={{ textTransform: 'none', alignSelf: matches ? 'auto' : 'center' }}
                            >
                                Descargar Excel
                            </Button>
                        </Box>
                    )}
                </Box>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: "150px 0" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <MaterialReactTable
                        columns={matches ? columns : responsiveColumns}
                        enableColumnResizing={matches ? false : true}
                        columnResizeMode="fixed"
                        enableFullScreenToggle={false}
                        data={translations}
                        localization={MRT_Localization_ES}
                        layoutMode='grid'
                    />
                )}
            </Box>
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: '8px',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography variant="h6" component="h2">
                        Observación
                    </Typography>
                    <Typography sx={{
                        mt: 2,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        {selectedObservacion}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            sx={{ textTransform: 'none' }}
                            onClick={handleCloseModal}>
                            Cerrar
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <ModalGeneral
                open={openDetailsModal}
                onClose={handleCloseDetailsModal}
                titulo={"Detalles de la Solicitud"}
                Contenido={<>
                    {selectedDetails ? (
                        <Box>
                            <Typography><strong>Título:</strong> {selectedDetails.title}</Typography>
                            <Typography><strong>Estado:</strong> {selectedDetails.status?.value}</Typography>
                            <Typography><strong>Fecha del pedido:</strong> {selectedDetails.createdAt?.split('T')[0]}</Typography>
                            <Typography><strong>Fecha de fin:</strong> {selectedDetails.deadline?.split('T')[0]}</Typography>
                            <Typography><strong>Observaciones:</strong> {selectedDetails.observation || "Sin observaciones"}</Typography>
                            <Box mt={3}>
                                <Typography variant="subtitle1" gutterBottom><strong>Archivos disponibles:</strong></Typography>

                                {selectedDetails.file_original?.file_name ? (
                                    <Button
                                        variant="outlined"
                                        sx={{ mr: 2, mb: 2, textTransform: 'none' }}
                                        startIcon={<DownloadIcon />}
                                        onClick={() =>
                                            handleDownloadFile(
                                                'original',
                                                selectedDetails.file_original.folder_name,
                                                selectedDetails.file_original.file_name
                                            )
                                        }
                                    >
                                        Descargar Original
                                    </Button>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Sin archivo original</Typography>
                                )}

                                {selectedDetails.file_corrected?.file_name ? (
                                    <Button
                                        variant="outlined"
                                        sx={{ mt: 1, textTransform: 'none' }}
                                        startIcon={<DownloadIcon />}
                                        onClick={() =>
                                            handleDownloadFile(
                                                'corrected',
                                                selectedDetails.file_corrected.folder_name,
                                                selectedDetails.file_corrected.file_name
                                            )
                                        }
                                    >
                                        Descargar Traducido
                                    </Button>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Sin archivo traducido</Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Typography>No hay datos que mostrar.</Typography>
                    )}
                </>}
            />
        </>
    );
};

export default EstadoSolicitudes;
